import { useState } from 'react';
import {
  Chip, IconButton, Tooltip, Stack, TextField, MenuItem, Menu, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Divider, Box, Alert, Snackbar,
} from '@mui/material';
import { Visibility, MoreVert, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { siteOnboardingApi, SITE_LIFECYCLE_STATUSES, type SiteApplication } from '../../../api/siteOnboarding.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');
const statusColor = (s: string): 'default' | 'success' | 'error' | 'warning' | 'info' =>
  s === 'completed' ? 'success' : s === 'cancelled' ? 'error' : ['on_hold'].includes(s) ? 'warning' : s === 'draft' ? 'default' : 'info';
const prioColor = (p?: string | null): 'default' | 'error' | 'warning' | 'info' =>
  p === 'critical' ? 'error' : p === 'high' ? 'warning' : p === 'low' ? 'default' : 'info';

export function SiteApplicationsPage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [viewId, setViewId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const canCreate = hasPermission('site_onboarding.create');
  const canSubmit = hasPermission('site_onboarding.submit');

  const { data, isLoading } = useQuery({ queryKey: ['site-applications', page, limit, status], queryFn: () => siteOnboardingApi.list({ page, limit, status: status || undefined }) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['site-applications'] });
  const run = (p: Promise<unknown>, msg: string) => p.then(() => { setToast(msg); refresh(); }).catch((e) => setToast(extractApiError(e)));
  const newApp = () => siteOnboardingApi.create({ site_name: 'New Site' }).then((a) => { refresh(); navigate(`/site-applications/${a.id}/edit`); }).catch((e) => setToast(extractApiError(e)));

  const columns: Column<SiteApplication>[] = [
    { key: 'application_code', label: 'Site ID', width: 150 },
    { key: 'site_name', label: 'Site' },
    { key: 'project_name', label: 'Project', render: (r) => (r.project_code ? `${r.project_code} — ${r.project_name ?? ''}` : '—') },
    { key: 'client_name', label: 'Client', render: (r) => r.client_name ?? '—' },
    { key: 'city', label: 'City', render: (r) => r.city ?? '—' },
    { key: 'priority', label: 'Priority', render: (r) => (r.priority ? <Chip size="small" color={prioColor(r.priority)} label={human(r.priority)} /> : '—') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={statusColor(r.status)} label={human(r.status)} /> },
    { key: 'delay_days', label: 'Delay', render: (r) => (Number(r.delay_days) > 0 ? <Chip size="small" color="warning" label={`${r.delay_days}d`} /> : '—') },
  ];

  return (
    <>
      <PageHeader title="Site Applications" subtitle="Site lifecycle — Draft → Survey → BOQ → Material → Installation → Testing → Commissioning → Handover → Completed."
        action={canCreate && <Button startIcon={<Add />} variant="contained" onClick={newApp}>New Site</Button>} />

      <Stack direction="row" mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 260 }}>
          <MenuItem value="">All</MenuItem>
          {['draft', ...SITE_LIFECYCLE_STATUSES].map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
        </TextField>
      </Stack>

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <RowActions row={r} perms={{ canSubmit, canCreate }}
            onView={() => setViewId(r.id)}
            onEdit={() => navigate(`/site-applications/${r.id}/edit`)}
            onSubmit={() => run(siteOnboardingApi.submit(r.id), 'Site created')}
            onDelete={() => run(siteOnboardingApi.remove(r.id), 'Draft deleted')}
            onCancel={() => run(siteOnboardingApi.cancel(r.id), 'Site cancelled')}
            onReopen={() => siteOnboardingApi.reopen(r.id).then(() => { setToast('Site reopened for editing'); refresh(); navigate(`/site-applications/${r.id}/edit`); }).catch((e) => setToast(extractApiError(e)))}
            onClone={() => siteOnboardingApi.clone(r.id).then((a) => { setToast('Cloned to a new draft'); refresh(); navigate(`/site-applications/${a.id}/edit`); }).catch((e) => setToast(extractApiError(e)))}
          />
        )} />

      {viewId && <ViewDialog id={viewId} onClose={() => setViewId(null)} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

interface Perms { canSubmit: boolean; canCreate: boolean }

function RowActions({ row, perms, onView, onEdit, onSubmit, onDelete, onCancel, onReopen, onClone }: {
  row: SiteApplication; perms: Perms;
  onView: () => void; onEdit: () => void; onSubmit: () => void; onDelete: () => void; onCancel: () => void; onReopen: () => void; onClone: () => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  const s = row.status;
  const items: Array<{ label: string; onClick: () => void; color?: string }> = [];
  if (s === 'draft') {
    if (perms.canSubmit) { items.push({ label: 'Edit', onClick: onEdit }, { label: 'Submit', onClick: onSubmit }); }
    if (perms.canCreate) items.push({ label: 'Delete', onClick: onDelete, color: 'error' });
  } else {
    // Any submitted/mid-lifecycle/completed/cancelled site can be reopened back to an editable draft.
    if (perms.canSubmit) items.push({ label: 'Reopen & Edit', onClick: onReopen });
    // Status is now driven automatically by Site Monitoring — no manual Change Status here.
    if (perms.canSubmit && s !== 'completed' && s !== 'cancelled') items.push({ label: 'Cancel Site', onClick: onCancel, color: 'warning' });
    if (perms.canCreate) items.push({ label: 'Clone', onClick: onClone });
  }

  return (
    <>
      <Tooltip title="View"><IconButton size="small" onClick={onView}><Visibility fontSize="small" /></IconButton></Tooltip>
      {items.length > 0 && (
        <>
          <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}><MoreVert fontSize="small" /></IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
            {items.map((it) => <MenuItem key={it.label} onClick={() => { close(); it.onClick(); }} sx={it.color ? { color: `${it.color}.main` } : undefined}>{it.label}</MenuItem>)}
          </Menu>
        </>
      )}
    </>
  );
}

function ViewDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: app } = useQuery({ queryKey: ['site-application', id], queryFn: () => siteOnboardingApi.get(id) });
  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{app ? `${app.application_code} — ${app.site_name}` : 'Loading…'}</DialogTitle>
      <DialogContent dividers>
        {app && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="Project" value={app.project_code ? `${app.project_code} — ${app.project_name ?? ''}` : null} />
              <Field label="Client" value={app.client_name} />
              <Field label="Type" value={human(app.site_type)} />
              <Field label="Priority" value={human(app.priority)} />
              <Box><Typography variant="caption" color="text.secondary">Status</Typography><Box><Chip size="small" color={statusColor(app.status)} label={human(app.status)} /></Box></Box>
            </Stack>
            <Field label="Address" value={[app.site_address, app.city, app.pincode].filter(Boolean).join(', ') || null} />
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="Geo" value={app.latitude != null ? `${app.latitude}, ${app.longitude}` : null} />
              {app.google_map_url ? <Box><Typography variant="caption" color="text.secondary">Map</Typography><Box><a href={app.google_map_url} target="_blank" rel="noopener noreferrer">View on Map</a></Box></Box> : null}
              <Field label="OEM / CPO" value={[app.oem, app.cpo].filter(Boolean).join(' / ') || null} />
              <Field label="Chargers" value={app.number_of_chargers != null ? `${app.number_of_chargers} × ${human(app.charger_type)} ${app.charger_capacity ?? ''}` : null} />
            </Stack>
            <Divider />
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="Allocation" value={app.allocation_date} />
              <Field label="Gigforce Committed" value={app.gigforce_committed_date} />
              <Field label="Actual Handover" value={app.actual_handover_date} />
              <Field label="Delay Days" value={String(app.delay_days ?? 0)} />
            </Stack>
            {app.site_id && <Alert severity="success">Site master #{app.site_id} created.</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return <Box><Typography variant="caption" color="text.secondary">{label}</Typography><Typography>{value ?? '—'}</Typography></Box>;
}
